'use client';

import Link from 'next/link';
import styles from './FoundersPreview.module.scss';
import { IconArrowRight, IconMail, IconMapPin } from '@tabler/icons-react';
import { gaCta } from '@/src/helpers/gtag';

export interface Founder {
  name: string;
  role: string;
  photo: string;
}

export interface FoundersPreviewProps {
  badge?: string;
  title?: string;
  subtitle?: string;
  founders: Founder[];
  image?: string;
  imageAlt?: string;
  ctaLabel?: string;
  ctaHref?: string;
  secondaryCtaLabel?: string;
  secondaryCtaHref?: string;
  note?: string;
  location?: string;
}

export function FoundersPreview({
  badge,
  title,
  subtitle,
  founders,
  image,
  imageAlt,
  ctaLabel,
  ctaHref,
  secondaryCtaLabel,
  secondaryCtaHref,
  note,
  location,
}: FoundersPreviewProps) {
  if (!founders || founders.length < 2) {
    return null;
  }

  const [first, second] = founders;

  return (
    <section className={styles.section} aria-labelledby="founders-title">
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.visualSide}>
            {image ? (
              <>
                <img
                  src={image}
                  alt={imageAlt || 'Rencontrons-nous'}
                  className={styles.visualImage}
                  loading="lazy"
                />
                <div className={styles.visualOverlay} />
              </>
            ) : (
              <div className={styles.portraitCollage}>
                <div className={styles.portraitBack}>
                  <img src={second.photo} alt={second.name} loading="lazy" />
                </div>
                <div className={styles.portraitFront}>
                  <img src={first.photo} alt={first.name} loading="lazy" />
                </div>
              </div>
            )}
            <span className={styles.contactPill}>
              <span className={styles.dot} aria-hidden="true" />
              {location || 'Disponibles pour échanger'}
            </span>
          </div>

          <div className={styles.contentSide}>
            {badge && (
              <span className={styles.badge}>
                <span className={styles.badgeLine} aria-hidden="true" />
                {badge}
              </span>
            )}
            {title && (
              <h2 id="founders-title" className={styles.title}>
                {title}
              </h2>
            )}
            {subtitle && <p className={styles.subtitle}>{subtitle}</p>}

            <div className={styles.founders}>
              <div className={styles.founderCard}>
                <div className={styles.avatar}>
                  <img src={first.photo} alt={first.name} loading="lazy" />
                </div>
                <div className={styles.founderInfo}>
                  <span className={styles.name}>{first.name}</span>
                  <span className={styles.role}>{first.role}</span>
                </div>
              </div>
              <div className={styles.founderCard}>
                <div className={styles.avatar}>
                  <img src={second.photo} alt={second.name} loading="lazy" />
                </div>
                <div className={styles.founderInfo}>
                  <span className={styles.name}>{second.name}</span>
                  <span className={styles.role}>{second.role}</span>
                </div>
              </div>
            </div>

            <div className={styles.ctaRow}>
              {ctaLabel && ctaHref && (
                <Link
                  href={ctaHref}
                  className={styles.ctaPrimary}
                  onClick={() => gaCta(ctaLabel, 'founders_preview_cta', ctaHref)}
                >
                  {ctaLabel}
                  <IconArrowRight size={18} aria-hidden="true" />
                </Link>
              )}
              {secondaryCtaLabel && secondaryCtaHref && (
                <Link
                  href={secondaryCtaHref}
                  className={styles.ctaSecondary}
                  onClick={() =>
                    gaCta(secondaryCtaLabel, 'founders_preview_secondary_cta', secondaryCtaHref)
                  }
                >
                  <IconMail size={18} aria-hidden="true" />
                  {secondaryCtaLabel}
                </Link>
              )}
            </div>

            {note && (
              <p className={styles.note}>
                <IconMapPin size={20} className={styles.noteIcon} aria-hidden="true" />
                <span>{note}</span>
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
